import React, { useContext, useMemo } from 'react';
import cx from 'classnames';

import { Link } from '@/components/atoms/Link/render';
import { Image } from '@/components/molecules/Image/render';
import { ButtonPrimary } from '@/components/atoms/ButtonPrimary/render';
import { Tag } from '@/components/atoms/Tag/render';
import { LocaleContext } from '@/contexts/locale-context';

import block from './block.json';
import { getStyles } from './styles.css';
import { CardTitleWithLink } from './CardTitleWithLink';

export const CardShow = React.forwardRef<HTMLDivElement, CardShowProps>(
	(
		{
			label,
			artist,
			title,
			subtitle,
			dates,
			location,
			image,
			categories = [],
			link,
			linkTicket,
			state,
			publics = [],
			variants = [],
		},
		ref
	) => {
		const rootClass = cx(
			'supt-card supt-cardShow',
			getStyles(),
			link?.href && 'supt-withLink',
			state && `-${state}`,
			variants.map((variant) => `-${variant}`)
		);

		const { __t } = useContext(LocaleContext);

		const linkProps = useMemo(
			() =>
				link?.href
					? {
							href: link?.href,
							attrs: link?.opensInNewTab && {
								target: '_blank',
								rel: 'noopener noreferrer',
							},
					  }
					: null,
			[link]
		);

		return (
			<div className={rootClass} data-block={block.slug} ref={ref}>
				<div className="supt-card__wrapper">
					{image?.src && (
						<div className="supt-cardShow__imageWrapper">
							{label?.text && (
								<div className="supt-cardShow__label">
									<p className="supt-cardShow__label__title">
										{label?.title ??
											__t(
												'card-show-label-title',
												'Around the show'
											)}
									</p>
									{!!label.text && (
										<p className="supt-cardShow__label__text">
											<span>{label.text}</span>
										</p>
									)}
								</div>
							)}
							<Image
								{...image}
								className="supt-cardShow__image"
							/>
							<div className="supt-cardShow__categories">
								{categories.map(({ title, type }, i) => {
									return (
										<div
											key={i}
											className="supt-cardShow__category"
										>
											<Tag title={title} type={type} />
										</div>
									);
								})}
							</div>
						</div>
					)}
					<CardTitleWithLink
						linkProps={linkProps}
						artist={artist}
						title={title}
					/>
					{subtitle && (
						<p
							className="supt-cardShow__subtitle"
							dangerouslySetInnerHTML={{ __html: subtitle }}
						/>
					)}
					<div className="supt-cardShow__details">
						<div className="supt-cardShow__day">
							<p
								className="supt-cardShow__day__info"
								dangerouslySetInnerHTML={{ __html: dates }}
							/>
							{state ? (
								<Tag
									title={__t(`state-${state}`, state)}
									isSquared
									type="alert"
								/>
							) : null}
						</div>
						<p
							className="supt-cardShow__location"
							dangerouslySetInnerHTML={{ __html: location }}
						/>
					</div>
					{!!publics.length && (
						<ul className="supt-cardShow__public-list">
							{publics.slice(0, 3).map(({ id, icon, title }) => (
								<li key={id}>
									<Image {...icon} />
								</li>
							))}
							{publics.length > 3 && (
								<li className="supt-cardShow__public-extra">
									{publics.length - 3}+
								</li>
							)}
						</ul>
					)}
					{linkTicket?.href && (
						<div className="supt-cardShow__ticket-wrapper">
							<div className="supt-cardShow__ticket">
								<ButtonPrimary
									{...linkTicket}
									title={
										linkTicket?.title ??
										__t('card-show-tickets', 'Billets')
									}
									className="supt-cardShow__ticket__button"
								/>
							</div>
						</div>
					)}
				</div>
			</div>
		);
	}
) as ForwardedCardShow;

CardShow.slug = block.slug;
CardShow.title = block.title;
